// Настройки уведомлений в профиле: какие группы событий присылать и по каким серверам.
// Изменения сохраняются сразу; при ошибке переключатель возвращается назад.
// Раздел раскрывается по нажатию на заголовок; настройки грузятся при раскрытии.
import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import {
  fetchAlertMutes,
  fetchNotificationSettings,
  saveNotificationSettings,
  unmuteAlertScope,
} from '../../services/notificationsService';
import {
  PUSH_DENIED,
  PUSH_IOS_INSTALL,
  PUSH_OFF,
  PUSH_ON,
  PUSH_UNSUPPORTED,
  disablePush,
  enablePush,
  getPushConfig,
  getPushState,
  pushAvailableHere,
} from '../../services/pushService';
import { extractErrorMessage } from '../../utils/errors';
import { useIikoCloudStatus } from '../../hooks/useIikoCloudStatus';
import CollapsibleSection from '../settings/CollapsibleSection';
import { ACCENT, ChoiceList, ERROR_COLOR, SectionTitle, ToggleRow } from '../settings/SettingsControls';

const pushButtonSx = {
  mt: 1,
  height: 40,
  px: 2,
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: 700,
  boxShadow: 'none',
  '&:hover': { boxShadow: 'none' },
};

const PUSH_HINTS = {
  [PUSH_UNSUPPORTED]: 'Этот браузер не поддерживает push-уведомления.',
  [PUSH_IOS_INSTALL]:
    'Push работает только в установленном приложении — см. «Установить приложение» выше.',
  [PUSH_DENIED]:
    'Уведомления для этого сайта запрещены. Разрешите их в настройках сайта (значок замка в адресной строке → «Уведомления») или в настройках уведомлений телефона, затем обновите страницу.',
};

// Push на этом устройстве. Не показывается в Telegram и если на сервере нет ключей VAPID
function PushBlock() {
  const [enabled, setEnabled] = useState(false);
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null); // { text } — ошибка включения/выключения

  useEffect(() => {
    if (!pushAvailableHere()) return undefined;
    let alive = true;
    getPushConfig()
      .then(async (config) => {
        if (!alive || !config.enabled) return;
        setEnabled(true);
        setState(await getPushState());
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  if (!enabled || !state) return null;

  const run = async (action) => {
    setBusy(true);
    setStatus(null);
    try {
      await action();
    } catch (err) {
      setStatus({ text: extractErrorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  // Разрешение запрашиваем только здесь — по нажатию
  const handleEnable = () => run(async () => setState(await enablePush()));
  const handleDisable = () => run(async () => setState(await disablePush()));

  return (
    <>
      <SectionTitle>Push-уведомления на этом устройстве</SectionTitle>
      {state === PUSH_ON ? (
        <>
          <ToggleRow id="notify-push" first label="Push на этом устройстве" checked disabled={busy} onChange={handleDisable} />
        </>
      ) : (
        <>
          {PUSH_HINTS[state] && (
            <Typography sx={{ fontSize: '14px', color: '#d4d4d8' }}>{PUSH_HINTS[state]}</Typography>
          )}
          {state === PUSH_OFF && (
            <Button
              onClick={handleEnable}
              disabled={busy}
              sx={{ ...pushButtonSx, color: '#1a0f0a', backgroundColor: ACCENT, '&:hover': { backgroundColor: '#ff8a65', boxShadow: 'none' } }}
            >
              {busy ? <CircularProgress size={18} sx={{ color: '#1a0f0a' }} /> : 'Включить'}
            </Button>
          )}
        </>
      )}
      {status && (
        <Typography role="alert" sx={{ mt: 1, fontSize: '13px', color: ERROR_COLOR, whiteSpace: 'pre-line' }}>
          {status.text}
        </Typography>
      )}
    </>
  );
}

export default function NotificationSettingsCard({ sx }) {
  return (
    <CollapsibleSection id="notifications" title="Уведомления" sx={sx}>
      <NotificationSettings />
    </CollapsibleSection>
  );
}

// Админ: отключённые оповещения о сбоях (по серверу iiko или ключу iikoCloud) — «Вернуть». Не админ (403)
// или отключённых нет — блока нет
function AlertMutesBlock() {
  const [mutes, setMutes] = useState([]);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    fetchAlertMutes()
      .then((list) => alive && setMutes(list))
      .catch(() => alive && setMutes([]));
    return () => {
      alive = false;
    };
  }, []);

  if (mutes.length === 0) return null;

  const unmute = async (scope) => {
    setBusy(scope);
    setError('');
    try {
      setMutes(await unmuteAlertScope(scope));
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setBusy('');
    }
  };

  return (
    <>
      <SectionTitle>Отключённые оповещения о сбоях</SectionTitle>
      {mutes.map((mute) => (
        <Box key={mute.scope} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, minHeight: 48 }}>
          <Typography sx={{ fontSize: '15px', color: '#f5f5f5', minWidth: 0, wordBreak: 'break-word' }}>{mute.title}</Typography>
          <Button
            onClick={() => unmute(mute.scope)}
            disabled={Boolean(busy)}
            sx={{ minHeight: 44, flexShrink: 0, textTransform: 'none', fontWeight: 600, color: ACCENT }}
          >
            Вернуть
          </Button>
        </Box>
      ))}
      {error && <Typography role="alert" sx={{ fontSize: '13px', color: ERROR_COLOR }}>{error}</Typography>}
    </>
  );
}

const SHIFT_SUMMARY_MODES = [
  { value: 'immediate', label: 'Сразу' },
  { value: 'morning', label: 'Утром в 9:00', hint: 'Сводка, пришедшая ночью (с 21:00 до 9:00), — в 9:00. Днём — сразу' },
];

function NotificationSettings() {
  const iikocloud = useIikoCloudStatus();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    fetchNotificationSettings()
      .then((data) => alive && setSettings(data))
      .catch((err) => alive && setError(extractErrorMessage(err)));
    return () => {
      alive = false;
    };
  }, []);

  const save = async (next) => {
    const previous = settings;
    setSettings(next);
    setSaving(true);
    setError('');
    try {
      setSettings(await saveNotificationSettings(next));
    } catch (err) {
      setSettings(previous);
      setError(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleGroup = (key, on) => {
    const groups = new Set(settings.enabled_groups);
    if (on) groups.add(key);
    else groups.delete(key);
    save({ ...settings, enabled_groups: settings.groups.map((g) => g.key).filter((k) => groups.has(k)) });
  };

  const toggleServer = (id, on) => {
    const disabled = new Set(settings.disabled_server_ids);
    if (on) disabled.delete(id);
    else disabled.add(id);
    save({ ...settings, disabled_server_ids: [...disabled].sort((a, b) => a - b) });
  };

  return (
    <Box>
      {!settings && !error && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={22} sx={{ color: ACCENT }} />
        </Box>
      )}

      {settings && (
        <>
          {settings.groups.map((group, index) => (
            <ToggleRow
              key={group.key}
              id={`notify-group-${group.key}`}
              first={index === 0}
              label={group.title}
              checked={settings.enabled_groups.includes(group.key)}
              disabled={saving}
              onChange={(on) => toggleGroup(group.key, on)}
            />
          ))}

          {/* Сводка после закрытия кассовой смены — по каждому ресторану */}
          <SectionTitle>Сводка после закрытия смены</SectionTitle>
          <ToggleRow
            id="notify-shift-summary"
            first
            label="Присылать сводку"
            hint="Выручка, чеки и средний чек по каждому ресторану после закрытия кассовой смены"
            checked={settings.shift_summary !== false}
            disabled={saving}
            onChange={(on) => save({ ...settings, shift_summary: on })}
          />
          <Box sx={{ mt: 0.5 }}>
            <ChoiceList
              label="Когда присылать сводку"
              value={settings.shift_summary_mode || 'immediate'}
              disabled={saving || settings.shift_summary === false}
              onChange={(mode) => save({ ...settings, shift_summary_mode: mode })}
              options={SHIFT_SUMMARY_MODES.map((option) => ({ ...option, disabled: settings.shift_summary === false }))}
            />
          </Box>

          {/* Склад (остатки iikoCloud) — у активного сервера с iikoCloud; выключенное видно всегда, чтобы вернуть */}
          {(iikocloud === true || settings.stock_negative === false) && (
            <>
              <SectionTitle>Склад</SectionTitle>
              <ToggleRow
                id="notify-stock-negative"
                first
                label="Новые минусы на складе"
                hint="Раз в день утром — какие позиции ушли в минус со вчерашнего дня (серверы с iikoCloud)"
                checked={settings.stock_negative !== false}
                disabled={saving}
                onChange={(on) => save({ ...settings, stock_negative: on })}
              />
            </>
          )}

          {settings.servers.length > 0 && (
            <>
              <SectionTitle>Серверы</SectionTitle>
              {settings.servers.map((server, index) => (
                <ToggleRow
                  key={server.id}
                  id={`notify-server-${server.id}`}
                  first={index === 0}
                  label={server.name}
                  checked={!settings.disabled_server_ids.includes(server.id)}
                  disabled={saving}
                  onChange={(on) => toggleServer(server.id, on)}
                />
              ))}
            </>
          )}
        </>
      )}

      <AlertMutesBlock />

      <PushBlock />

      {error && (
        <Typography role="alert" sx={{ mt: 1, fontSize: '13px', color: ERROR_COLOR, whiteSpace: 'pre-line' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}
